import { RootState, AppThunk } from "ducks/state";
import { createSelector } from "reselect";
import { newNotification } from "./notification";
import { hen, Hen } from "@udok/lib/internal/store";
import {
  DoctorPreference,
  Preferences,
  HealthcareLicense,
  DoctorPreferenceForm,
} from "@udok/lib/api/models";
import {
  createDoctorPreference,
  fetchDoctorPreference,
  fetchLicenseTypes,
  fetchListDoctorPreference,
} from "@udok/lib/api/doctor";
import { getToken, UNAUTHORIZED } from "./auth";

export type InitialState = {
  preferenceByID: { [prefID: string]: DoctorPreference | undefined };
  licenseTypes: Array<HealthcareLicense>;
};

// Reducers
const initialState: InitialState = {
  preferenceByID: {},
  licenseTypes: [],
};

class DoctorPreferenceSlice extends Hen<InitialState> {
  prefLoaded(pref: DoctorPreference) {
    this.state.preferenceByID[pref.prefID] = pref;
  }

  prefsLoaded(prefs: DoctorPreference[]) {
    prefs.forEach((pref) => {
      this.state.preferenceByID[pref.prefID] = pref;
    });
  }

  licenseTypesLoaded(t: Array<HealthcareLicense>) {
    this.state.licenseTypes = t;
  }
}

export const [Reducer, actions] = hen(new DoctorPreferenceSlice(initialState), {
  [UNAUTHORIZED]: () => {
    return initialState;
  },
});

// Selectors
const mainSelector = (state: RootState) => state.doctorPreferences;
export const preferenceSelector = (state: RootState) =>
  state.doctorPreferences.preferenceByID;

export const getPreferenceRepo = createSelector(mainSelector, (state) => ({
  preferenceByID: state.preferenceByID ?? {},
}));

export const licenseTypesView = createSelector(mainSelector, (state) => {
  return {
    list: state.licenseTypes ?? [],
  };
});

export const getOneDoctorPreferences = (props: { prefID: string }) =>
  createSelector(mainSelector, (state) => {
    return {
      location: state.preferenceByID[props.prefID],
    };
  });

export const getAllDoctorPreferences = createSelector(mainSelector, (state) => {
  return {
    list: Object.keys(state.preferenceByID).map(
      (prefID) => state.preferenceByID[prefID]
    ),
  };
});

//Actions
export function createOneDoctorPreference(
  pref: DoctorPreferenceForm,
  section?: string
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return createDoctorPreference(apiToken, pref)
      .then((r) => {
        dispatch(actions.prefLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification(section ?? "general", {
            status: "error",
            message: (e.response?.data?.error || e).message,
          }) as any
        );
        throw e;
      });
  };
}

export function fetchOneDoctorPreference(
  prefID: Preferences
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    const codedID = encodeURIComponent(prefID);
    return fetchDoctorPreference(apiToken, codedID)
      .then((r) => {
        dispatch(actions.prefLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: (e.response?.data?.error || e).message,
          }) as any
        );
      });
  };
}

export function fetchAllDoctorPreference(): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;

    return fetchListDoctorPreference(apiToken)
      .then((r) => {
        dispatch(actions.prefsLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          }) as any
        );
      });
  };
}

export function loadLicenseTypes(): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    return fetchLicenseTypes()
      .then((r) => {
        dispatch(actions.licenseTypesLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: "Falha ao carregar conselho profissional",
          }) as any
        );
        throw e;
      });
  };
}
